import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
	MAT_DIALOG_DATA,
	MatDialogActions,
	MatDialogClose,
	MatDialogContent,
	MatDialogRef,
	MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatInput } from '@angular/material/input';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Relative } from '../../../../api/app-types/relative.app';
import { ClientService } from '../../../../api/services/client.api';
import { LvrField } from '../../../../core/components/field/field';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import { LvrButton } from '../../../../core/components/lvr-button/lvr-button';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-client-relatives-anonymization-dialog',
	templateUrl: './client-relatives-anonymization-dialog.html',
	imports: [
		MatDialogTitle,
		MatDialogContent,
		MatDialogActions,
		MatDialogClose,
		MatFormField,
		MatInput,
		FormsModule,
		LvrInputDirective,
		LvrField,
		TranslocoPipe,
		LvrButton,
	],
	providers: [{ provide: ClientService, useClass: ClientService }],
})
export class ClientRelativesAnonymizationDialog {
	private readonly api = inject(ClientService);
	private readonly dialogRef = inject(MatDialogRef<ClientRelativesAnonymizationDialog>);
	private readonly ntfySrv = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly data: { relative: Relative; clientId: number; onAnonymized: () => Promise<void> } =
		inject(MAT_DIALOG_DATA);
	public readonly relative = this.data.relative;

	public readonly confirmationInput = signal('');
	public readonly loading = signal(false);

	public readonly isDeactivateEnabled = computed(() => {
		return this.confirmationInput().trim() === this.relative.lastName.trim();
	});

	public async deactivateUser() {
		if (!this.isDeactivateEnabled()) {
			return;
		}

		this.loading.set(true);

		try {
			await this.api.deleteRelative(this.data.clientId, this.relative.id);
			this.ntfySrv.addNotification(
				createSuccessNotification(
					this.transloco.translate('base.notification_message.relatives_anonymized', {
						firstName: this.relative.firstName,
						lastName: this.relative.lastName,
					}),
				),
			);
			await this.data.onAnonymized();
			this.dialogRef.close(true);
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (_: unknown) {
			this.ntfySrv.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.relatives_anonymization_error')),
			);
		} finally {
			this.loading.set(false);
		}
	}
}
